import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { Validators } from '@angular/forms'
import { disabled, form } from '@angular/forms/signals'
import { MatSnackBar } from '@angular/material/snack-bar'
import {
  ApixDownloadLaskutustiedotPdfRequest,
  ApixDownloadLaskutustiedotPdfResponse,
  ApixMaksutAktivoiVastaanottoRequest,
  ApixMaksutAktivoiVastaanottoResponse,
  ApixMaksutHalutaanOttaaKayttoonRequest,
  ApixMaksutHalutaanOttaaKayttoonResponse,
  ApixReceivedInvoiceConfig,
} from '@lemon/core-firebase/model/apix'
import { SahkoisenLaskunValittajaService } from '@lemon/core-firebase/service/lasku/sahkoisen-laskun-valittaja.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { Observable, Subject, firstValueFrom, map, of as observableOf, switchMap, takeUntil } from 'rxjs'

import { MatDialog } from '@angular/material/dialog'
import { ActivatedRoute, Router } from '@angular/router'
import { NewFeature } from '@lemon/core-firebase/model/kayttaja'
import { LaskunSahkoinenOsoite } from '@lemon/core-firebase/model/lasku'
import { TuettuKieli } from '@lemon/core/model/common'
import { UserSettingsService } from 'app/_angular/service/user-settings.service'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { TiedostojenLataamisService } from '../_jaettu-angular/service/tiedostojen-lataamis.service'
import { MaksutLopetaVastaanottoDialog, MaksutLopetaVastaanottoDialogData } from './maksut-lopeta-vastaanotto.dialog'
import { MaksutVastaanottoAloitettuDialog, MaksutVastaanottoAloitettuDialogData } from './maksut-vastaanotto-aloitettu.dialog'

interface LaskunSahkoinenOsoiteJaNimi extends LaskunSahkoinenOsoite {
  sahkoinenValittajaNimi: string
}
interface VastaanottoLomake {
  vastaanottoKayttoon: boolean
}

interface MaksuLomake {
  maksuominaisuusKayttoon: boolean
}

@Component({
  templateUrl: './maksut-asetukset.component.html',
  styleUrls: ['./maksut-asetukset.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MaksutAsetuksetComponent implements OnInit, OnDestroy {
  readonly viewState = signal<'maksaminen' | 'vastaanotto'>('vastaanotto')
  readonly maksamisenTila = signal<'maksaminen-tilattu' | 'maksaminen-ei-paalla' | 'maksaminen-paalla'>('maksaminen-ei-paalla')
  readonly vastaanotonTila = signal<'vastaanotto-paalla' | 'vastaanotto-ei-paalla'>('vastaanotto-ei-paalla')
  readonly lataaminenValmis = signal(false)

  commonError: string

  private readonly _maksuominaisuusPoissa = signal(false)

  vastaanottoLomake = form(signal<VastaanottoLomake>({ vastaanottoKayttoon: false }), polku => {
    validoiReaktiivisesti(polku.vastaanottoKayttoon, Validators.requiredTrue)
  })
  maksuLomake = form(signal<MaksuLomake>({ maksuominaisuusKayttoon: false }), polku => {
    validoiReaktiivisesti(polku.maksuominaisuusKayttoon, Validators.requiredTrue)
    disabled(polku.maksuominaisuusKayttoon, () => this._maksuominaisuusPoissa())
  })

  apixLaskuosoiteObservable: Observable<LaskunSahkoinenOsoiteJaNimi>

  showNewFeatureSignAtMaksutMaksaminenObservable: Observable<boolean>

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private _saveStarted: boolean = false

  namename = 'sdfg' + Math.random()

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _dialog: MatDialog,
    private _formValidationService: FormValidationService,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _translationService: LemonTranslationService,
    private _sahkoisenLaskunValittajaService: SahkoisenLaskunValittajaService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _kayttajaService: KayttajaService,
    private _snackBar: MatSnackBar,
    private _fileSaverService: FileSaverService,
    private _userSettingsService: UserSettingsService,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
  ) {}

  ngOnInit(): void {
    this._route.params.pipe(takeUntil(this._ngUnsubscribe)).subscribe(params => {
      if (params.tyyppi === 'vastaanotto') {
        this.viewState.set('vastaanotto')
      } else if (params.tyyppi === 'maksaminen') {
        // this.updateSeen(NewFeature.MAKSUT_MAKSAMINEN)
        this.viewState.set('maksaminen')
      }
    })

    // this.operaattorit = this._sahkoisenLaskunValittajaService.annaKaikkiValittajat()
    const apixInvoiceConfigObservable = this._kayttajaService.kayttajanTiedotObservable.pipe(
      switchMap(kayttajanTiedot => {
        if (!kayttajanTiedot?.asiakasAvain) {
          return observableOf(null)
        }
        return this._firebaseLemonaid
          .firestoreDoc<ApixReceivedInvoiceConfig>('customers/' + kayttajanTiedot.asiakasAvain + '/apix-received-invoice-config/' + kayttajanTiedot.asiakasAvain)
          .listenSnap()
      }),
    )

    apixInvoiceConfigObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asetukset => {
      if (asetukset) {
        this.lataaminenValmis.set(true)
        const config = asetukset.data()
        let maksamisenTila: 'maksaminen-tilattu' | 'maksaminen-ei-paalla' | 'maksaminen-paalla' = 'maksaminen-ei-paalla'
        // this.maksamisenTila = 'maksaminen-tilattu'
        // this.maksamisenTila = 'maksaminen-paalla'
        if (config?.paymentServiceOrdered) {
          maksamisenTila = 'maksaminen-tilattu'
        }
        if (config?.paymentIsActive) {
          maksamisenTila = 'maksaminen-paalla'
        }
        this.maksamisenTila.set(maksamisenTila)

        asetaArvo(this.maksuLomake.maksuominaisuusKayttoon, !!config?.paymentServiceOrdered)
        // this.vastaanotonTila = 'maksaminen-tilattu'
        // this.vastaanottoKayttoonFormControl.setValue(true)

        this.vastaanotonTila.set(config?.paymentReceiveIsActive ? 'vastaanotto-paalla' : 'vastaanotto-ei-paalla')
        asetaArvo(this.vastaanottoLomake.vastaanottoKayttoon, !!config?.paymentReceiveIsActive)
        this._maksuominaisuusPoissa.set(this.vastaanotonTila() !== 'vastaanotto-paalla')
        // this.vastaanotonTila = 'vastaanotto-tilattu'
        // this.vastaanottoKayttoonFormControl.setValue(true)
      } else {
        this.lataaminenValmis.set(false)
      }
    })

    this.apixLaskuosoiteObservable = apixInvoiceConfigObservable.pipe(
      map(snapshot => {
        const config = snapshot?.data()
        if (config?.receiveActive && config?.paymentReceiveIsActive && config?.receiveAddress) {
          const osoiteJaNimi: LaskunSahkoinenOsoiteJaNimi = {
            sahkoinenOsoite: config.receiveAddress.sahkoinenOsoite,
            sahkoinenValittaja: config.receiveAddress.sahkoinenValittaja,
            sahkoinenValittajaNimi: this._sahkoisenLaskunValittajaService.annaValittajanNimi(config.receiveAddress.sahkoinenValittaja),
          }
          return osoiteJaNimi
        }
        return null
      }),
    )

    // this.showNewFeatureSignAtMaksutMaksaminenObservable = this._userSettingsService.userSettingsObservable.pipe(
    //   takeUntil(this._ngUnsubscribe),
    //   map(newFeatures => {
    //     if (!newFeatures?.seen[NewFeature.MAKSUT_MAKSAMINEN]) {
    //       return true
    //     }
    //     return false
    //   })
    // )
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  async downloadPdf(kieli: TuettuKieli) {
    this._ladataanService.aloitaLataaminen()

    try {
      const asiakas = await firstValueFrom(this._kayttajaService.nykyinenAsiakasObservable)

      const reqData: ApixDownloadLaskutustiedotPdfRequest = {
        kieli: kieli,
      }

      const resp = await this._firebaseLemonaid.functionsCall<ApixDownloadLaskutustiedotPdfRequest, ApixDownloadLaskutustiedotPdfResponse>('downloadLaskutustiedotPdf', reqData)

      if (!resp || resp.e) {
        throw new Error('Laskutustiedot PDF download failed! ' + (resp?.e ?? 'No response data')) // Handle in catch
      }

      if (!resp.base64File?.length) {
        throw new Error('Laskutustiedot PDF download failed! PDF file empty')
      }

      const output = this._tiedostojenLataamisService.base64ToUint8Array(resp.base64File)
      const laskutustiedotLocalised = this._translationService.lokalisoiKielella('laskutustiedot-pdf.laskutustiedot', reqData.kieli)
      const fileName = laskutustiedotLocalised + ' ' + asiakas.nimi + ' (' + asiakas.ytunnus + ')' + '.pdf'
      this._fileSaverService.saveAs(new Blob([output], { type: 'application/pdf' }), fileName)
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackBar.open(this._translationService.lokalisoi('tositteet.laskujen-vastaanotto.pdf-epaonnistui'), 'OK', { duration: 10 * 1000 })
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async updateSeen(seenFeature: NewFeature) {
    const kayttajanTiedot = await firstValueFrom(this._kayttajaService.kayttajanTiedotObservable)
    if (!kayttajanTiedot?.asiakasAvain) {
      return
    }

    const showNewFeatureSignAtMaksutMaksaminen = await firstValueFrom(this.showNewFeatureSignAtMaksutMaksaminenObservable)
    if (showNewFeatureSignAtMaksutMaksaminen) {
      return this._userSettingsService.setFeatureSeen(seenFeature, kayttajanTiedot.asiakasAvain, kayttajanTiedot.uid, this._firebaseLemonaid)
    }
  }

  vaihdaValilehtiVastaanottoon() {
    window.scrollTo(0, 0)
    this._router.navigate(['maksut', 'asetukset', 'vastaanotto'])
  }

  vaihdaValilehtiMaksamiseen() {
    window.scrollTo(0, 0)
    this._router.navigate(['maksut', 'asetukset', 'maksaminen'])
  }

  async tallennaMaksamisenAktivointi() {
    if (!this.vastaanottoLomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.vastaanottoLomake)
      return
    }

    if (this._saveStarted) {
      return
    }

    this._saveStarted = true
    this._ladataanService.aloitaLataaminen()

    try {
      const reqData: ApixMaksutHalutaanOttaaKayttoonRequest = {}
      const resp = await this._firebaseLemonaid.functionsCall<ApixMaksutHalutaanOttaaKayttoonRequest, ApixMaksutHalutaanOttaaKayttoonResponse>('apixMaksutHalutaanOttaaKayttoon', reqData)

      if (!resp || resp.e) {
        /** Throw error to end up in the catch. */
        throw new Error('Maksut halutaan ottaa käyttöön virhe! ' + (resp?.e || 'unknown-error'))
      }
    } catch (err) {
      this.commonError = this._translationService.lokalisoi('yleiset.tuntematon-virhe')
      this._errorHandler.handleError(err)
    } finally {
      this._saveStarted = false
      this._ladataanService.lopetaLataaminen()
    }
  }

  async tallennaVastaanotonLopetus() {
    if (this.vastaanottoLomake.vastaanottoKayttoon().value()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.vastaanottoLomake)
      return
    }

    const data: MaksutLopetaVastaanottoDialogData = {}
    const dialogRef = this._dialog.open<MaksutLopetaVastaanottoDialog, MaksutLopetaVastaanottoDialogData, void>(MaksutLopetaVastaanottoDialog, { data: data })
  }

  async tallennaVastaanotonAktivointi() {
    if (!this.vastaanottoLomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.vastaanottoLomake)
      return
    }

    if (this._saveStarted) {
      return
    }

    this._saveStarted = true
    this._ladataanService.aloitaLataaminen()

    try {
      const reqData: ApixMaksutAktivoiVastaanottoRequest = {}
      const resp = await this._firebaseLemonaid.functionsCall<ApixMaksutAktivoiVastaanottoRequest, ApixMaksutAktivoiVastaanottoResponse>('apixAktivoiLaskujenVastaanotto', reqData)

      if (!resp || resp.e) {
        /** Throw error to end up in the catch. */
        throw new Error('Forwarded address save failed! ' + (resp?.e || 'unknown-error'))
      } else {
        const data: MaksutVastaanottoAloitettuDialogData = {}
        this._dialog.open(MaksutVastaanottoAloitettuDialog, { data: data })
      }
    } catch (err) {
      this.commonError = this._translationService.lokalisoi('yleiset.tuntematon-virhe')
      this._errorHandler.handleError(err)
    } finally {
      this._saveStarted = false
      this._ladataanService.lopetaLataaminen()
    }
  }
}
